// needs-input.ts — (TF-6b-1; planning 10-16 20:40 design §1) the "Needs you" card's rules, pure: what each question says, to
// an admin (the facts, labelled) and to a customer (a line, never the engine's reason), the money line, which buttons, and
// the card's state after an answer. The shapes are contracts' NeedsInput (C1.4c-1 outcome, C1.4c-2 eyes and questionId);
// every newer field is read defensively, so the card works on a backend that doesn't send it yet.
// (B64) the counts in words are contracts' own repsInWords, from the browser entry (`@vms/contracts/browser`, no Node
//  imports); the copy this file kept until then is retired.
import { repsInWords, REP_MOVEMENT_WORDS } from "@vms/contracts/browser";
// (B107) the customer's words — the titles, the buttons, the line, the answers — are read through w() (Admin → Words);
//  the admin's labelled facts are operator-only and stay as they are
import { w } from "@/lib/words.ts";

export type NeedsInputCode = "claim_unsubstantiated" | "asset_required_but_absent" | "outcome_not_reached" | "eyes_fixed_stare" | (string & {});
type Pose = string | null;
export interface OutcomeFacts {
  beatId: string; movement: string;
  planned: { reps: number; startPose: Pose; endPose: string };
  measured: { reps: number | null; startPose: Pose; endPose: Pose };
  retakes: number; retakeSpentCents: number; limit: string;
  moreRetakes: { count: number; centsShown: number } | null;
  replan: { plannedOutcome: { kind: "rep_count"; value: number; endPose: string; movement: string; startPose: Pose };
    episode: { seriesId: string; seq: number; outcome: string } | null } | null;
}
export interface EyesFacts {
  beatId: string; longestNoBlinkMs: number; maxNoBlinkSec: number; blinks: number; retakes: number; retakeSpentCents: number; limit: string;
  moreRetakes: { count: number; centsShown: number };
}
export interface NeedsInputView {
  questionId?: string | null; stepId: string; beatId?: string | null; code?: NeedsInputCode; reason: string; missing: string[];
  options: string[]; openedAt?: string; expiresAt: string | null; outcome?: OutcomeFacts | null; eyes?: EyesFacts | null;
}

/** "$1.40" — the cost the engine SHOWS (cents), as dollars. */
export const money = (cents: number): string => `$${(Math.max(0, cents) / 100).toFixed(2)}`;
/** contracts' repsInWords for a known movement ("5 push-ups"); otherwise the count and the movement as named */
export const reps = (n: number, movement: string): string =>
  movement in REP_MOVEMENT_WORDS ? repsInWords(n, movement as keyof typeof REP_MOVEMENT_WORDS) : `${n} × ${movement.replace(/_/g, " ")}`;
const pose = (p: Pose) => (p === "up" ? w("page.needs_you.pose.up") : p === "down" ? w("page.needs_you.pose.down") : p ?? "—");
/** The limit that stopped the retakes (contracts OUTCOME_RETAKE_LIMITS), in words. */
export const LIMIT_WORDS: Record<string, string> = {
  off: "retakes are switched off", retakes_per_beat: "the most retakes for one shot", cents_per_beat: "the most to spend on one shot",
  cents_per_order: "the most to spend on retakes for this video", approved_cost: "the cost approved for this video", price_unknown: "a retake's price isn't known",
};
const limitText = (l: string) => LIMIT_WORDS[l] ?? l;

export interface CardButton { choice: string; label: string; note?: string }
export interface Card { title: string; lines: string[]; buttons: CardButton[]; noAnswer?: { label: string; href: string } }

/** (tf3-35b) one unit: video credits, as the app's header (100 ledger credits = 1), one decimal, rounded up as the backend's
 *  "This needs …" is */
const credits = (n: number) => w("page.needs_you.cost.credits", { n: (Math.ceil(n / 10) / 10).toFixed(1) });

/** Pure: the card for this question. Only the options the question lists; a spending one only with its cost to show.
 *  (tf3-35a, A-F9) `retakeCredits` is given on the video page, where the account's credits pay: a retake's cost is in
 *  credits there (null: retakes aren't priced yet, so no button). Left out (the admin's route), the engine's dollars. */
export function cardFor(ni: NeedsInputView, admin: boolean, retakeCredits?: number | null): Card {
  const o = ni.outcome ?? null, e = ni.eyes ?? null;
  const has = (c: string) => ni.options.includes(c);
  const buttons: CardButton[] = [];
  const more = (o?.moreRetakes ?? e?.moreRetakes) ?? null;
  const byCredits = retakeCredits !== undefined;
  const cost = (m: { centsShown: number }) => (byCredits ? credits(retakeCredits ?? 0) : w("page.needs_you.cost.dollars", { cost: money(m.centsShown) }));
  const canRetake = has("more_retakes") && !!more && (!byCredits || retakeCredits !== null);
  if (canRetake && more) buttons.push({ choice: "more_retakes", label: w("page.needs_you.try_again", { cost: cost(more) }),
    note: w(more.count === 1 ? "page.needs_you.more_tries.one" : "page.needs_you.more_tries.many", { n: more.count, cost: cost(more) }) });
  const noRetakes = has("more_retakes") && !!more && !canRetake;   // offered, but not priced for this account yet
  if (has("replan_outcome") && o?.replan) {
    const p = o.replan.plannedOutcome;
    buttons.push({ choice: "replan_outcome", label: w("page.needs_you.replan", { reps: reps(p.value, p.movement), pose: pose(p.endPose) }),
      ...(o.replan.episode ? { note: w("page.needs_you.replan_episode") } : {}) });
  }
  if (has("keep_clip")) buttons.push({ choice: "keep_clip", label: w("page.needs_you.keep_clip") });
  if (has("topic_angle")) buttons.push({ choice: "topic_angle", label: w("page.needs_you.topic_angle"), note: w("page.needs_you.topic_angle_note") });

  if (ni.code === "outcome_not_reached" && o) {
    const line = noRetakes ? w("page.needs_you.outcome.line.no_retakes")
      : canRetake && more ? w("page.needs_you.outcome.line.retake", { cost: cost(more) }) : w("page.needs_you.outcome.line.retake_unpriced");
    return { title: w("page.needs_you.outcome.title"), buttons, lines: admin ? [
      `Beat ${o.beatId}: ${o.movement.replace(/_/g, " ")} — planned ${reps(o.planned.reps, o.movement)} ending ${pose(o.planned.endPose)}; measured ${o.measured.reps === null ? "no count" : reps(o.measured.reps, o.movement)} ending ${pose(o.measured.endPose)}`,
      `${o.retakes} retake${o.retakes === 1 ? "" : "s"} made, ${money(o.retakeSpentCents)}`,
      `Stopped by: ${limitText(o.limit)}`,
    ] : [line] };
  }
  if (ni.code === "eyes_fixed_stare" && e) {
    return { title: w("page.needs_you.eyes.title"), buttons, lines: admin ? [
      `Beat ${e.beatId}: no blink for ${(e.longestNoBlinkMs / 1000).toFixed(1)} s (the limit is ${e.maxNoBlinkSec} s), ${e.blinks} blink${e.blinks === 1 ? "" : "s"} counted`,
      `${e.retakes} retake${e.retakes === 1 ? "" : "s"} made, ${money(e.retakeSpentCents)}`,
      `Stopped by: ${limitText(e.limit)}`,
    ] : [noRetakes ? w(has("keep_clip") ? "page.needs_you.eyes.line.no_retakes_keep" : "page.needs_you.eyes.line.no_retakes")
      : w(has("keep_clip") ? "page.needs_you.eyes.line.retake_keep" : "page.needs_you.eyes.line.retake", { cost: cost(e.moreRetakes) })] };
  }
  // the evidence gaps (missing business details): the specialist's list; its reason to admins only, labelled
  return { title: w("page.needs_you.missing.title"), buttons, lines: [...ni.missing, ...(admin && ni.reason ? [`Engine: ${ni.reason}`] : [])],
    ...(has("topic_angle") ? {} : { noAnswer: { label: w("page.needs_you.pick_less"), href: "/videos/new" } }) };
}

/** Pure: the card's state after an answer — "sent" while the same question is still shown (the run hasn't moved on),
 *  "changed" when another question took its place, null otherwise. */
export function sentState(sent: { questionId: string | null } | null, ni: NeedsInputView | null): "sent" | "changed" | null {
  if (!sent) return null;
  if (!ni) return null;
  if ((ni.questionId ?? null) === sent.questionId) return "sent";
  return "changed";
}

/** Pure: an answer's error, for the card: a changed question re-reads; "not done; try again" otherwise as given. */
export function answerError(e: unknown): { changed: boolean; text: string } {
  const x = e as { status?: number; code?: string; message?: string } | null;
  if (x?.code === "QUESTION_CHANGED" || (x?.status === 409 && /question changed/i.test(x?.message ?? "")))
    return { changed: true, text: w("page.needs_you.changed") };
  // (tf3-35c; A-F6) the workers' codes that mean the question shown is no longer the one to answer: said, and read again
  if (x?.code && (REREAD.has(x.code) || BACKEND_REREAD.has(x.code))) return { changed: true, text: x.message || w("page.needs_you.moved_on") };
  return { changed: false, text: x?.message || w("page.needs_you.not_done") };
}
/** contracts NEEDS_INPUT_ANSWER_REFUSAL_CODES that mean the card must read the question again (the test checks they're
 *  contracts'), and (session audit 10-17-B, B4) the backend's own that mean the same: the retake no longer offered, or not
 *  priced any more — the card then drops the button */
export const REREAD = new Set(["NO_SUCH_QUESTION", "ALREADY_CLOSED", "ALREADY_ANSWERED", "NOT_WAITING", "STEP_MOVED_ON", "NOT_OFFERED"]);
export const BACKEND_REREAD = new Set(["NO_RETAKES_OFFERED", "RETAKES_NOT_PRICED"]);
